import { useEffect, useState } from "react";
import { api } from "../api";
import { Modal } from "./Modal";
import type { LeadView, Manager } from "../types";

/**
 * Передача клиента другому менеджеру. Новый ответственный сразу получает
 * сообщение в телеграм с карточкой и комментарием.
 */
export function TransferModal({
  lead,
  onClose,
  onDone,
}: {
  lead: LeadView;
  onClose: () => void;
  onDone: (lead: LeadView, toName: string) => void;
}) {
  const [managers, setManagers] = useState<Manager[]>([]);
  const [to, setTo] = useState<number | "">("");
  const [comment, setComment] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    api
      .managers()
      .then((list) => setManagers(list.filter((m) => m.id !== lead.owner_user_id)))
      .catch((e) => setError(e.message));
  }, [lead.owner_user_id]);

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (to === "") return;
    setBusy(true);
    setError("");
    try {
      const updated = await api.transferLead(lead.meeting_id, to, comment.trim());
      onDone(updated, managers.find((m) => m.id === to)?.name ?? "");
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
      setBusy(false);
    }
  };

  const title = [lead.company, lead.contact_name].filter(Boolean).join(" — ") || "клиента";

  return (
    <Modal
      title={`Передать ${title}`}
      onClose={onClose}
      footer={
        <>
          <button onClick={onClose}>Отмена</button>
          <button
            className="primary"
            form="transfer-form"
            type="submit"
            disabled={busy || to === ""}
          >
            {busy && <span className="spinner" />} Передать
          </button>
        </>
      }
    >
      <form id="transfer-form" onSubmit={submit}>
        <p className="small muted" style={{ marginTop: 0 }}>
          Сейчас ведёт: <strong>{lead.owner_name ?? "никто"}</strong>
          {lead.recorded_by_name && lead.recorded_by_name !== lead.owner_name && (
            <> · переговоры вёл(а) {lead.recorded_by_name}</>
          )}
        </p>

        <div className="field">
          <label htmlFor="t-to">Кому</label>
          <select
            id="t-to"
            autoFocus
            value={to}
            onChange={(e) => setTo(e.target.value ? Number(e.target.value) : "")}
          >
            <option value="">Выберите менеджера</option>
            {managers.map((m) => (
              <option key={m.id} value={m.id}>
                {m.name}
              </option>
            ))}
          </select>
        </div>

        <div className="field">
          <label htmlFor="t-comment">Комментарий — необязательно</label>
          <textarea
            id="t-comment"
            rows={3}
            maxLength={500}
            placeholder="Например: клиент ждёт прайс до пятницы, просил звонить после 15:00"
            value={comment}
            onChange={(e) => setComment(e.target.value)}
          />
        </div>

        <p className="small muted">
          Новый менеджер сразу получит сообщение в телеграм с карточкой клиента
          и этим комментарием. Передача попадёт в историю работы с клиентом.
        </p>

        {error && <div className="error-text">{error}</div>}
      </form>
    </Modal>
  );
}
